import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useContainerWidth from '@/components/useContainerWidth';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtMoney(v) {
  if (v == null) return '—';
  return `$${Math.round(v).toLocaleString()}`;
}
function fmtAxis(v) {
  if (v >= 1000) return `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return `$${Math.round(v)}`;
}
function fmtDate(d) {
  const dt = new Date(`${d}T00:00:00`);
  return `${MONTHS[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
}
function fmtTick(d, years) {
  const dt = new Date(`${d}T00:00:00`);
  return years === 1 ? `${MONTHS[dt.getMonth()]} '${String(dt.getFullYear()).slice(2)}` : `${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}

// lines: [{ ticker, color, points: [[date, value], ...] }]
// scale: 'linear' | 'log' (equal pixel = equal % move) | 'gap' (each line minus the first ticker, in $)
export default function TrueReturnChart({ lines, years, animKey = 0, scale = 'linear', endAligned = null }) {
  const containerRef = useRef(null);
  const width = useContainerWidth(containerRef);
  const pathRefs = useRef({});
  const [hoverIdx, setHoverIdx] = useState(null);
  // Phone only: weekly (every 5th close) keeps 3–4 lines readable; Daily restores every close.
  const [weekly, setWeekly] = useState(true);

  const data = useMemo(() => {
    if (!lines.length) return null;
    const dateSet = new Set();
    lines.forEach((l) => l.points.forEach(([d]) => dateSet.add(d)));
    const dates = [...dateSet].sort();
    if (dates.length < 2) return null;
    const idxOf = new Map(dates.map((d, i) => [d, i]));
    const gap = scale === 'gap' && lines.length >= 2;
    const baseMap = gap ? new Map(lines[0].points) : null;
    // plotted values per line (gap mode subtracts the first ticker's value on the same day)
    const plotted = lines.map((l, li) => {
      if (!gap) return l.points;
      if (li === 0) return l.points.filter(([d]) => baseMap.has(d)).map(([d]) => [d, 0]);
      return l.points.filter(([d]) => baseMap.has(d)).map(([d, v]) => [d, v - baseMap.get(d)]);
    });
    const vals = plotted.flatMap((pts) => pts.map((p) => p[1]));
    const useLog = scale === 'log';
    const minLin = Math.min(...vals), maxLin = Math.max(...vals);
    const yMin = useLog ? Math.max(1, minLin * 0.98) : gap ? Math.min(minLin, 0) : minLin;
    const yMax = useLog ? Math.max(maxLin * 1.02, yMin * 1.05) : gap ? Math.max(maxLin, 0) : maxLin;
    const l10 = (v) => Math.log10(Math.max(v, 1));
    const phone = width < 600;
    const height = phone ? 320 : 280;
    const padLeft = 62, padRight = 12, padTop = 12, padBottom = phone ? 44 : 30;
    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;
    const xAt = (i) => padLeft + (i / (dates.length - 1)) * chartW;
    const yAt = (v) => {
      const frac = useLog
        ? (l10(v) - l10(yMin)) / ((l10(yMax) - l10(yMin)) || 1)
        : (v - yMin) / ((yMax - yMin) || 1);
      return padTop + chartH - frac * chartH;
    };
    const series = lines.map((l, li) => {
      const byDate = new Map(l.points);
      const pts = plotted[li].map(([d, v]) => ({ x: xAt(idxOf.get(d)), y: yAt(v), v, d }));
      // Phone: draw one point per trading week (every 5th close, always
      // ending on the latest) so four lines don't fuse into a solid band.
      // Hover/legend still use every daily point.
      const drawn = phone && weekly && pts.length > 60
        ? pts.filter((_, i) => i % 5 === 0 || i === pts.length - 1)
        : pts;
      return { ...l, byDate, plotMap: new Map(plotted[li]), pts, d: drawn.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') };
    });
    const yTicks = [];
    for (let i = 0; i <= 4; i++) {
      const v = useLog ? Math.pow(10, l10(yMin) + ((l10(yMax) - l10(yMin)) * i) / 4) : yMin + ((yMax - yMin) * i) / 4;
      yTicks.push({ label: gap ? (v >= 0 ? `+${fmtAxis(v)}` : `−${fmtAxis(-v)}`) : fmtAxis(v), y: yAt(v) });
    }
    const step = Math.max(1, Math.floor(dates.length / (phone ? 4 : 6)));
    const xTicks = [];
    for (let i = 0; i < dates.length; i += step) xTicks.push({ label: fmtTick(dates[i], years), x: xAt(i) });
    const baseVal = gap ? 0 : 10000;
    const baseY = yAt(baseVal);
    return { dates, series, yTicks, xTicks, width, height, padLeft, padTop, chartW, chartH, gap, baseY: baseY >= padTop && baseY <= padTop + chartH ? baseY : null, xAt };
  }, [lines, width, years, scale, weekly]);

  useEffect(() => {
    Object.values(pathRefs.current).forEach((el) => {
      if (!el) return;
      try {
        const len = el.getTotalLength();
        if (!Number.isFinite(len) || len <= 0) return;
        el.style.transition = 'none';
        el.style.strokeDasharray = `${len}`;
        el.style.strokeDashoffset = `${len}`;
        requestAnimationFrame(() => { el.style.transition = 'stroke-dashoffset 1.8s ease-out'; el.style.strokeDashoffset = '0'; });
      } catch { /* jsdom */ }
    });
  }, [animKey, data]);

  const locate = useCallback((clientX) => {
    if (!data || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const svgX = ((clientX - rect.left) / rect.width) * data.width;
    const frac = (svgX - data.padLeft) / data.chartW;
    const idx = Math.round(Math.min(1, Math.max(0, frac)) * (data.dates.length - 1));
    setHoverIdx(idx);
  }, [data]);

  if (!data) return null;
  const idx = hoverIdx ?? data.dates.length - 1;
  const hoverDate = data.dates[idx];
  const hoverX = data.xAt(idx);

  return (
    <div data-testid="true-return-chart">
      <div
        ref={containerRef}
        className="relative w-full select-none"
        style={{ touchAction: 'pan-y' }}
        onMouseMove={(e) => locate(e.clientX)}
        onMouseLeave={() => setHoverIdx(null)}
        onTouchStart={(e) => locate(e.touches[0].clientX)}
        onTouchMove={(e) => locate(e.touches[0].clientX)}
        onTouchEnd={() => setHoverIdx(null)}
        data-testid="true-return-chart-svg"
      >
        <svg viewBox={`0 0 ${data.width} ${data.height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
          {data.yTicks.map((t, i) => <line key={i} x1={data.padLeft} y1={t.y} x2={data.padLeft + data.chartW} y2={t.y} className="stroke-border/30" strokeDasharray="3,3" />)}
          {data.yTicks.map((t, i) => <text key={`y${i}`} x={data.padLeft - 6} y={t.y + 3} textAnchor="end" className="fill-muted-foreground" fontSize="10" fontFamily="IBM Plex Mono, monospace">{t.label}</text>)}
          {data.xTicks.map((t, i) => <text key={`x${i}`} x={t.x} y={data.height - 6 - (data.width < 600 && i % 2 === 0 ? 12 : 0)} textAnchor={t.x > data.padLeft + data.chartW - 28 ? 'end' : t.x < data.padLeft + 28 ? 'start' : 'middle'} className="fill-muted-foreground" fontSize="10" fontFamily="IBM Plex Mono, monospace">{t.label}</text>)}
          {data.baseY != null && <line x1={data.padLeft} y1={data.baseY} x2={data.padLeft + data.chartW} y2={data.baseY} className="stroke-muted-foreground/50" strokeWidth="1" />}
          {data.series.map((s) => (
            <path key={s.ticker} ref={(el) => { pathRefs.current[s.ticker] = el; }} d={s.d} fill="none" stroke={s.color} strokeWidth={data.width < 600 ? 1.5 : 2} strokeLinejoin="round" strokeLinecap="round" data-testid={`true-return-line-${s.ticker}`} />
          ))}
          {hoverIdx != null && (
            <>
              <line x1={hoverX} y1={data.padTop} x2={hoverX} y2={data.padTop + data.chartH} stroke="#888" strokeWidth="1" strokeDasharray="4,2" />
              {data.series.map((s) => {
                const p = s.pts.find((q) => q.d === hoverDate);
                return p ? <circle key={`dot-${s.ticker}`} cx={p.x} cy={p.y} r="4" fill={s.color} stroke="white" strokeWidth="1.5" /> : null;
              })}
            </>
          )}
        </svg>
      </div>
      {data.width < 600 && (
        <div className="mt-1.5 flex items-center justify-end gap-2" data-testid="true-return-density">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Line detail</span>
          <div className="inline-flex items-center rounded-full border bg-muted/40 p-0.5">
            {[['weekly', 'Weekly'], ['daily', 'Daily']].map(([key, lbl]) => (
              <button
                key={key}
                type="button"
                onClick={() => setWeekly(key === 'weekly')}
                aria-pressed={weekly === (key === 'weekly')}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${weekly === (key === 'weekly') ? 'bg-sky-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`true-return-density-${key}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 rounded-lg border bg-muted/30 px-3 py-2" data-testid="true-return-legend">
        <p className="text-[11px] font-mono text-muted-foreground tabular-nums" data-testid="true-return-legend-date">
          {hoverIdx != null ? fmtDate(hoverDate) : `${fmtDate(hoverDate)} (latest)`}
        </p>
        {hoverIdx == null && endAligned?.missing?.length > 0 && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5" data-testid="true-return-end-aligned-note">
            {endAligned.missing.join(', ')} {endAligned.missing.length > 1 ? 'have' : 'has'} no close yet for {fmtDate(endAligned.latest)} — showing {fmtDate(endAligned.date)} for all tickers.
          </p>
        )}
        <div className={`mt-1 grid gap-2 ${data.series.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
          {data.series.map((s) => {
            const v = s.byDate.get(hoverDate);
            return (
              <div key={s.ticker} className="min-w-0" data-testid={`true-return-legend-${s.ticker}`}>
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
                  <span className="font-mono font-bold text-sm" style={{ color: s.color }}>{s.ticker}</span>
                  <span className="text-[10px] text-muted-foreground">{s.drip ? 'DRIP' : 'no DRIP'}</span>
                </div>
                <div className="font-mono text-base sm:text-lg tabular-nums" data-testid={`true-return-legend-value-${s.ticker}`}>
                  {v == null ? <span className="text-muted-foreground text-sm">{s.startDate && hoverDate < s.startDate ? 'not yet listed' : 'no data'}</span> : fmtMoney(v)}
                  {data.gap && v != null && (() => {
                    const g = s.plotMap.get(hoverDate);
                    if (g == null) return null;
                    return <span className={`ml-2 text-xs ${g > 0 ? 'text-emerald-600 dark:text-emerald-400' : g < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`} data-testid={`true-return-legend-gap-${s.ticker}`}>{g === 0 ? 'base' : `${g > 0 ? '+' : '−'}${fmtMoney(Math.abs(g)).slice(0)}`}</span>;
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
